// @flow

import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import Dummy from './Dummy';
import ChaseField from './ChaseField';
import Timer from './Timer';
import Restart from './Restart';
import { updateArray, shuffleArray } from '../utils';
import useGameState, { STATES } from '../utils/use_game_state';
import Game from '../Shared/Game';

const ChaseGame = (props) => {
  const mistakeCross = [
    `${props.assetPath}assets/games/chase_game/element_x.png`,
    `${props.assetPath}assets/games/chase_game/element_x-alpha.png`,
  ];
  const startImg = `${props.assetPath}assets/games/chase_game/playknop-tikkertje.png`;

  const [dummies, setDummies] = useState([false, false, false, false, false]);
  const [introGame, setIntroGame] = useState(true);
  const [seconds, setSeconds] = useState(60);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [mistakes, setMistakes] = useState(0);
  const [dummyUrls, setDummyUrls] = useState(props.images);
  const [roundTimer, setRoundTimer] = useState(null);
  const [gameState, setGameDone, setGamePlay] = useGameState();

  const dummiesRef = useRef(dummies);
  dummiesRef.current = dummies;

  const level = (currentScore) => {
    if (currentScore < 4) return 1;
    if (currentScore < 12) return 2;
    if (currentScore < 24) return 3;
    if (currentScore < 40) return 4;
    return 5;
  };

  const updateDummiesAfter = (index, millis) => {
    const timer = setTimeout(() => {
      setDummies(updateArray(dummiesRef.current, index, true));
    }, millis);
    return () => clearTimeout(timer);
  };

  // Main game loop
  useEffect(() => {
    // Check if we are supposed to be in a round and all dummies are empty
    if (!gameOver && !introGame && !dummies.some(item => item)) {
      // Give the player a second to get ready
      setTimeout(() => {
        // Create a new set of dummies
        setDummyUrls(u => shuffleArray(u));
        const shuffleDummies = (lvl) => {
          const randomIndices = shuffleArray(_.sampleSize([0, 1, 2, 3, 4], lvl));
          randomIndices.map((randomIndex, index) => (
            updateDummiesAfter(randomIndex, 300 * index)
          ));
        };
        shuffleDummies(level(score));

        // Set a timer for this round
        const timeout = ((level(score) * 1000) + 3000) - (score * 100);
        const timer = window.setTimeout(() => {
          // The round times has ended, this means not all dummies were
          // clicked in time. We count a mistake.
          setMistakes(m => m + 1);
          // Then we restart the round
          setDummies([false, false, false, false, false]);
        }, timeout);
        setRoundTimer(timer);
      }, 1000);
    }
  }, [dummies, gameOver, introGame, score]);

  const restartGame = () => {
    setDummies([false, false, false, false, false]);
    setIntroGame(false);
    setSeconds(60);
    setScore(0);
    setGameOver(false);
    setMistakes(0); // => Triggers effect below

    setGamePlay();
  };

  useEffect(() => {
    if (introGame) {
      const audioClip = document.createElement('audio');
      audioClip.src = props.audio.intro;
      audioClip.play();
    }
  }, [props.audio.intro, introGame]);

  useEffect(() => {
    if (gameOver && gameState < STATES.DONE) {
      const audioClip = document.createElement('audio');
      audioClip.src = props.audio.again;
      audioClip.play();
      setGameDone();
      setDummies([false, false, false, false, false]);
    }
  }, [gameOver, props.audio.again, setGameDone, gameState]);

  useEffect(() => {
    if (mistakes > 0 && !gameOver) {
      const audioClip = document.createElement('audio');
      audioClip.src = props.audio.wrong;
      audioClip.play();
      if (mistakes >= 3) {
        setGameOver(true);
      }
    }
  }, [mistakes, props.audio.wrong, gameOver]);

  const dummyClicked = (num) => {
    if (dummies[num]) {
      const audioClip = document.createElement('audio');
      audioClip.src = props.audio.good;
      audioClip.play();
      setScore(score + 1);
      const newDummies = updateArray(dummies, num, false);
      setDummies(newDummies);
      if (!newDummies.some(item => item)) {
        window.clearTimeout(roundTimer);
        setRoundTimer(null);
      }
    }
  };

  useEffect(() => {
    if (seconds <= 0) {
      setGameOver(true);
      return;
    }

    if (!introGame && !gameOver) {
      /* eslint-disable */
      const timer = setTimeout(() => {
        setSeconds(seconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
      /* eslint-enable */
    }
  }, [seconds, introGame, gameOver]);

  const renderMistake = iteration => (
    <img
      key={iteration}
      alt={'kruis'}
      className="chase__mistake-img"
      src={mistakeCross[iteration < mistakes ? 0 : 1]}
    />
  );

  const renderDummies = () =>
    dummies.map((state, i) =>
      (<Dummy
        active={state}
        key={i + 1}
        dummy={i + 1}
        imageUrl={dummyUrls[i]}
        onClick={(e) => {
          e.stopPropagation();
          dummyClicked(i);
        }}
      />),
    );

  return (
    <Game
      gameState={gameState}
      relatedGames={props.relatedGames}
      smallRelated={true}
      disableBackdropBeforeDone={true}
      assetPath={props.assetPath}
      isApp={props.isApp}
    >
      {gameState < STATES.DONE
        ?
          <div className="flex items-center h-full">
            <div className="ps__game-background-sky flex-1">
              <div className="chase__game-main">
                <div className="chase__game">
                  {renderDummies()}
                  <Timer seconds={seconds} />
                  <div className="chase__mistake-box flex">{_.times(3, i => renderMistake(i))}</div>
                  {(gameOver || introGame) && (
                    <div className="chase__gameover-overlay" onClick={restartGame}>
                      <img src={startImg} alt={'Start knop'} className="chase__restart-btn" />
                    </div>
                  )}
                  <ChaseField assetPath={props.assetPath} />
                </div>
              </div>
            </div>
          </div>
        :
          <Restart
            score={score}
            childCard={props.childCard}
            startImg={startImg}
            restartGame={restartGame}
          />}
    </Game>
  );
};

const chaseElement = document.querySelector('#chase');

if (chaseElement) {
  const data = chaseElement.dataset;
  const props = {
    back: data.back,
    title: data.title,
    images: JSON.parse(data.images),
    audio: JSON.parse(data.audio),
    assetPath: data.assetPath,
    relatedGames: JSON.parse(data.related),
    childCard: JSON.parse(data.card),
    isApp: data.app === 'true',
  };
  ReactDOM.render(<ChaseGame {...props} />, chaseElement);
}
